<template>
  <div class="ciencia-container">
    <!-- Skeleton Loader -->
    <div v-if="loading">
      <!-- Skeleton para el primer post -->
      <div class="featured-skeleton">
        <div class="skeleton-image-large"></div>
        <div class="skeleton-date"></div>
        <div class="skeleton-category-center"></div>
        <div class="skeleton-title-center"></div>
        <div class="skeleton-excerpt-center"></div>
      </div>
      <!-- Skeleton para el resto -->
      <div class="posts-list">
        <div v-for="i in 5" :key="i" class="skeleton-post">
          <div class="skeleton-image"></div>
          <div class="skeleton-content">
            <div class="skeleton-category"></div>
            <div class="skeleton-title"></div>
            <div class="skeleton-title short"></div>
            <div class="skeleton-date"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Contenido real -->
    <div v-else-if="posts.length > 0">
      <!-- PRIMER POST: imagen arriba, luego fecha, categoría, título, extracto -->
      <div 
        v-if="posts[0]"
        class="featured-post"
        @click="goToPost(posts[0])"
      >
        <img 
          :src="getImageUrl(posts[0].image)" 
          :alt="posts[0].title" 
          class="featured-image"
        >
        <div class="featured-date">{{ formatDate(posts[0].created_at) }}</div>
        <span class="featured-category">{{ posts[0].category?.name || 'Ciencia y Tecnología' }}</span>
        <h2 class="featured-title">{{ posts[0].title }}</h2>
        <p class="featured-excerpt">{{ posts[0].extract }}</p>
      </div>

      <!-- RESTO DE POSTS: lista normal -->
      <div class="posts-list">
        <div 
          v-for="post in posts.slice(1)" 
          :key="post.id"
          class="post-item"
          @click="goToPost(post)"
        >
          <img :src="getImageUrl(post.image)" :alt="post.title" class="post-image">
          <div class="post-content">
            <div class="post-meta">
              <span class="post-category">{{ post.category?.name || 'Ciencia y Tecnología' }}</span>
              <span class="post-date">{{ formatDate(post.created_at) }}</span>
            </div>
            <h3 class="post-title">{{ post.title }}</h3>
            <p class="post-excerpt">{{ post.extract }}</p>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="empty-posts">
      No hay publicaciones de ciencia y tecnología disponibles
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import api from '../services/api'

const router = useRouter()
const posts = ref([])
const loading = ref(true)

const getImageUrl = (image) => {
  if (!image) return '/noimagen.png'
  if (image.startsWith('http')) return image
  return `https://portal.jlhvision.com/posts/${image}`
}

const formatDate = (date) => {
  if (!date) return ''
  const d = new Date(date)
  const now = new Date()
  const diff = Math.floor((now - d) / 1000 / 60 / 60)
  
  if (diff < 1) return 'Hace unos minutos'
  if (diff < 24) return `Hace ${diff} horas`
  if (diff < 48) return 'Ayer'
  return d.toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

const loadPosts = async () => {
  loading.value = true
  try {
    const response = await api.get('/ciencia-y-tecnologia')
    posts.value = response.data || []
  } catch (error) {
    console.error('Error cargando posts de ciencia y tecnología:', error)
    posts.value = []
  } finally {
    loading.value = false
  }
}

const goToPost = (post) => {
  router.push(`/noticia/${post.slug}`)
}

onMounted(() => {
  loadPosts()
})
</script>

<style scoped>
.ciencia-container {
  width: 100%;
  margin: 15px 0;
}

/* ========== PRIMER POST (IMAGEN ARRIBA) ========== */
.featured-post {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  margin-bottom: 25px;
  text-align: center;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
  padding-bottom: 20px;
}

.featured-post:active {
  transform: scale(0.99);
}

.featured-image {
  width: 100%;
  max-height: 400px;
  object-fit: cover;
  object-position: center;
}

.featured-date {
  font-size: 12px;
  color: #a50000;
  margin-top: 15px;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.featured-category {
  display: inline-block;
  background: transparent;
  color: #a50000;
  font-size: 12px;
  padding: 0;
  margin: 0 0 10px 0;
  text-transform: uppercase;
  font-weight: bold;
  letter-spacing: 0.5px;
}

.featured-title {
  font-size: 22px;
  font-weight: bold;
  color: #1a1a1a;
  margin: 0 15px 15px 15px;
  line-height: 1.3;
}

.featured-excerpt {
  font-size: 15px;
  color: #555;
  line-height: 1.5;
  margin: 0 15px;
}

/* ========== LISTA NORMAL (resto de posts) ========== */
.posts-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.post-item {
  display: flex;
  gap: 15px;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s;
  padding: 0;
}

.post-item:active {
  transform: scale(0.99);
}

.post-image {
  width: 120px;
  height: 120px;
  object-fit: cover;
  object-position: center;
  flex-shrink: 0;
  border-radius: 12px;
}

.post-content {
  flex: 1;
  padding: 8px 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.post-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.post-category {
  display: inline-block;
  background: transparent;
  color: #a50000;
  font-size: 11px;
  padding: 0;
  margin: 0;
  text-transform: uppercase;
  font-weight: bold;
  letter-spacing: 0.5px;
}

.post-title {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.post-excerpt {
  font-size: 13px;
  color: #666;
  line-height: 1.4;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.post-date {
  font-size: 11px;
  color: #999;
  margin: 0;
}

/* ========== SKELETON ========== */
.featured-skeleton {
  background: white;
  border-radius: 16px;
  margin-bottom: 25px;
  text-align: center;
  overflow: hidden;
  padding-bottom: 20px;
}

.skeleton-image-large {
  width: 100%;
  height: 250px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.skeleton-date {
  width: 100px;
  height: 12px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
  margin: 15px auto 8px auto;
}

.skeleton-category-center {
  width: 120px;
  height: 14px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
  margin: 0 auto 10px auto;
}

.skeleton-title-center {
  width: 80%;
  height: 24px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 6px;
  margin: 0 auto 15px auto;
}

.skeleton-excerpt-center {
  width: 70%;
  height: 40px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 6px;
  margin: 0 auto;
}

.skeleton-post {
  display: flex;
  gap: 15px;
  background: white;
  border-radius: 12px;
  padding: 0;
}

.skeleton-image {
  width: 120px;
  height: 120px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 12px;
  flex-shrink: 0;
}

.skeleton-content {
  flex: 1;
  padding: 8px 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.skeleton-category {
  width: 100px;
  height: 14px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-title {
  width: 90%;
  height: 18px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-title.short {
  width: 60%;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

.empty-posts {
  text-align: center;
  padding: 40px;
  color: #999;
  font-size: 14px;
}

/* Responsive */
@media (max-width: 768px) {
  .featured-title {
    font-size: 18px;
  }
  
  .featured-excerpt {
    font-size: 13px;
  }
  
  .featured-image {
    max-height: 250px;
  }
}

@media (max-width: 480px) {
  .featured-title {
    font-size: 16px;
  }
  
  .featured-excerpt {
    font-size: 12px;
  }
  
  .featured-image {
    max-height: 200px;
  }
  
  .post-image {
    width: 100px;
    height: 100px;
  }
  
  .post-title {
    font-size: 14px;
  }
  
  .post-excerpt {
    font-size: 12px;
  }
  
  .post-category,
  .post-date {
    font-size: 10px;
  }
}
</style>